import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { 
    House, 
    Buildings, 
    Users, 
    Upload, 
    Code, 
    ChartBar,
    SignOut,
    List,
    CaretDown,
    User,
    Export,
    GearSix,
    EnvelopeSimple,
    ChartLine,
    Globe
} from '@phosphor-icons/react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LANGUAGES = [
    { code: 'de', label: 'Deutsch', flag: 'DE' },
    { code: 'en', label: 'English', flag: 'EN' },
    { code: 'zh', label: '中文', flag: '中' },
];

export function Layout({ children }) {
    const { t, i18n } = useTranslation();
    const { user, logout, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const navItems = [
        { path: '/dashboard', labelKey: 'nav.dashboard', icon: ChartBar },
        { path: '/analytics', labelKey: 'nav.analytics', icon: ChartLine },
        { path: '/properties', labelKey: 'nav.properties', icon: House },
        { path: '/brokers', labelKey: 'nav.brokers', icon: Buildings },
        { path: '/inquiries', labelKey: 'nav.inquiries', icon: EnvelopeSimple },
        { path: '/import', labelKey: 'nav.import', icon: Upload },
        { path: '/export', labelKey: 'nav.export', icon: Export },
        { path: '/embed-config', labelKey: 'nav.embed', icon: Code },
        { path: '/ftp-sync', labelKey: 'nav.settings', icon: GearSix },
    ];

    const adminItems = [
        { path: '/users', labelKey: 'nav.users', icon: Users },
    ];

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem('lang', lang);
    };

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

    return (
        <div className="min-h-screen bg-[#F8F9FA]">
            {/* Top Navbar */}
            <nav className="sticky top-0 z-50 bg-white border-b border-border h-16" data-testid="navbar">
                <div className="h-full px-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="p-2 hover:bg-muted rounded-sm transition-colors"
                            data-testid="sidebar-toggle"
                        >
                            <List size={20} weight="bold" />
                        </button>
                        <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo">
                            <House size={28} weight="fill" className="text-primary" />
                            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                OpenImmo Manager
                            </span>
                        </Link>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Language Switcher */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-9 px-3" data-testid="lang-switcher">
                                    <Globe size={16} />
                                    <span className="text-xs font-medium">{currentLang.flag}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {LANGUAGES.map((lang) => (
                                    <DropdownMenuItem
                                        key={lang.code}
                                        onClick={() => changeLanguage(lang.code)}
                                        className={`cursor-pointer ${i18n.language === lang.code ? 'bg-muted font-medium' : ''}`}
                                        data-testid={`lang-${lang.code}`}
                                    >
                                        <span className="mr-2 font-medium">{lang.flag}</span>
                                        {lang.label}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* User Menu */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                                    </div>
                                    <span className="hidden sm:inline">{user?.name}</span>
                                    <CaretDown size={16} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <div className="px-2 py-1.5">
                                    <p className="text-sm font-medium">{user?.name}</p>
                                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                                    <p className="text-xs text-muted-foreground capitalize mt-1">
                                        {user?.role === 'admin' ? 'Administrator' : 'Makler'}
                                    </p>
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild className="cursor-pointer" data-testid="profile-menu-btn">
                                    <Link to="/profile" className="flex items-center">
                                        <User className="mr-2" size={16} />
                                        {t('nav.profile')}
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                                    <SignOut className="mr-2" size={16} />
                                    {t('nav.logout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </nav>

            <div className="flex">
                {/* Sidebar */}
                <aside 
                    className={`${sidebarOpen ? 'w-64' : 'w-0 -ml-64'} transition-all duration-200 bg-white border-r border-border min-h-[calc(100vh-4rem)] fixed lg:sticky top-16 z-40`}
                    data-testid="sidebar"
                >
                    <nav className="p-4 space-y-1">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const active = isActive(item.path);
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                                        active 
                                            ? 'bg-primary text-primary-foreground font-medium' 
                                            : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                    }`}
                                    data-testid={`nav-${item.path.slice(1)}`}
                                >
                                    <Icon size={20} weight={active ? 'fill' : 'regular'} />
                                    <span>{t(item.labelKey)}</span>
                                </Link>
                            );
                        })}

                        {isAdmin && (
                            <>
                                <div className="pt-4 pb-2">
                                    <p className="px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        Admin
                                    </p>
                                </div>
                                {adminItems.map((item) => {
                                    const Icon = item.icon;
                                    const active = isActive(item.path);
                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                                                active 
                                                    ? 'bg-primary text-primary-foreground font-medium' 
                                                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                            }`}
                                            data-testid={`nav-${item.path.slice(1)}`}
                                        >
                                            <Icon size={20} weight={active ? 'fill' : 'regular'} />
                                            <span>{t(item.labelKey)}</span>
                                        </Link>
                                    );
                                })}
                            </>
                        )}
                    </nav>
                </aside>

                {/* Main Content */}
                <main className={`flex-1 p-6 ${sidebarOpen ? 'lg:ml-0' : ''}`}>
                    {children}
                </main>
            </div>
        </div>
    );
}
